import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community'
import numeral from 'numeral'
import { intl } from '../../../../i18n'
import { DefaultCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer/DefaultCellRenderer'
import { ColumnType } from '../../../containers/commons/AgGrid'
import { DateVO, dateVoService } from '../../../../domain/value-object/DateVO'
import { dateV2Service } from '../../../../domain/value-object/DateV2'
import { ALERT_COLOR, TimeGrain } from '../../../../lib/functions/report'
import { generateUuid } from '../../../../utils/uuids'
import { roundNumber } from '../../../../utils/number'
import { AggregateField } from '../../../../domain/entity/WbsItemEntity'
import { OrganizationWorkingDayCalendarDetail } from '../../../../lib/functions/organizationWorkingDayCalendar'
import {
  SprintReportKey,
  SprintReportSummarizeValue,
} from '../../../../lib/functions/sprintReport'
import { TotalCellRenderer } from '../components/cellRenderer/TotalCellRenderer'
import { SprintReportRow } from '../model/sprintReportRow'
import { SprintReportPageState } from '../hooks/pageState'
import { SprintReportSearchCondition } from '../hooks/searchCondition'
import { WorkLoadUnitState } from '../../../hooks/useWorkloadUnit'
import { summarizeValues } from '../lib/reportValueSummarizer'
import { isValid as isValidUrl } from '../../../../utils/urls'
import { open } from '../../../router'
import store from '../../../../store'
import { MessageLevel, addScreenMessage } from '../../../../store/messages'

const NO_VALUE_STR = '-'
const DISPLAY_FORMAT_WORKLOAD = '0,0.00'
const DISPLAY_FORMAT_COUNT = '0,0'

type DateRange = { from: DateVO; to: DateVO }

const RATING_KEYS: readonly SprintReportKey[] = [
  SprintReportKey.RESOURCE_USED_RATE,
  SprintReportKey.VELOCITY,
  SprintReportKey.THROUGHPUT,
] as const

const WORKLOAD_ONLY_KEYS: readonly SprintReportKey[] = [
  SprintReportKey.TOTAL_ACTUAL_HOUR,
  SprintReportKey.RESOURCE_USED_RATE,
  SprintReportKey.VELOCITY,
] as const

const isRatingCell = (row: SprintReportRow): boolean =>
  RATING_KEYS.includes(row.key)

const isWorkloadOnlyCell = (row: SprintReportRow): boolean =>
  WORKLOAD_ONLY_KEYS.includes(row.key)

export const createTotalReportColumnDef = (
  dateTerm: DateRange,
  searchCondition: SprintReportSearchCondition,
  workingDayCalendars: OrganizationWorkingDayCalendarDetail[]
): ColGroupDef =>
  // 旧 BulkSheet 版に合わせて Total 列は左ピン留めにせず、日付列と同じ流動列として扱う。
  // 左端は rowNumber / 担当者 / ステータスの 3 列のみピン留めする。
  createReportColumnDef({
    colId: 'total',
    field: 'total',
    dateTerm,
    pinned: false,
    lockPosition: undefined,
    headerName: 'Total',
    headerClass: '',
    searchCondition,
    workingDayCalendars,
  })

export const createDateReportColumnDef = (
  dateTerm: DateRange,
  searchCondition: SprintReportSearchCondition,
  workingDayCalendars: OrganizationWorkingDayCalendarDetail[]
): ColGroupDef => {
  const { timeGrain } = searchCondition
  const from = dateVoService.toDate(dateTerm.from)
  const to = dateVoService.toDate(dateTerm.to)
  const now = new Date()
  const isCurrentTerm = from <= now && now <= to
  const fromStr = dateVoService.format(dateTerm.from)
  return createReportColumnDef({
    colId: fromStr,
    field: fromStr,
    dateTerm,
    pinned: false,
    lockPosition: undefined,
    headerName: getHeaderName(dateTerm.from, timeGrain),
    headerClass: isCurrentTerm ? 'sevend-ag-grid-date-header-current' : '',
    searchCondition,
    workingDayCalendars,
  })
}

const getHeaderName = (date: DateVO, timeGrain: TimeGrain): string => {
  switch (timeGrain) {
    case TimeGrain.DAY:
      return dateVoService.format(date, 'M/D (ddd)')
    case TimeGrain.WEEK:
      return `${dateVoService.format(date, 'M/D')}${intl.formatMessage({
        id: 'wbsProgressLog.week',
      })}`
    case TimeGrain.TWO_WEEKS: {
      const end = dateV2Service.addDays(dateVoService.toDate(date), 13)
      return `${dateVoService.format(date, 'M/D')}-${dateVoService.format(
        dateVoService.construct(end),
        'M/D'
      )}`
    }
    case TimeGrain.MONTH:
      return `${dateVoService.format(date, 'M')}${intl.formatMessage({
        id: 'wbsProgressLog.month',
      })}`
    default:
      return ''
  }
}

const getSubHeaderName = (
  businessDays: number,
  workHours: number,
  timeGrain: TimeGrain
): string => {
  if (!businessDays) return '-'
  switch (timeGrain) {
    case TimeGrain.WEEK:
    case TimeGrain.TWO_WEEKS:
    case TimeGrain.MONTH:
      return `${businessDays}d / ${workHours}h`
    case TimeGrain.DAY:
      return `${workHours}h`
    default:
      return '-'
  }
}

const getCellColor = (
  value: SprintReportSummarizeValue | undefined
): string | undefined => {
  if (!value || !value.denominator) return undefined
  const rate = value.numerator / value.denominator
  if (0 < rate && rate <= 0.5) return ALERT_COLOR.LOW
  if (0.5 < rate && rate <= 0.75) return ALERT_COLOR.MIDDLE_LOW
  if (1.25 <= rate && rate < 1.5) return ALERT_COLOR.MIDDLE_HIGH
  if (1.5 <= rate) return ALERT_COLOR.HIGH
  return undefined
}

const openWorkResult = (
  values: SprintReportRow['values'],
  dateRange: { from: Date; to: Date }
) => {
  const sum = summarizeValues(
    values,
    dateRange,
    AggregateField.WBS_ITEM_WORKLOAD
  )
  const codes = (sum.wbsItemCodes ?? []).filter(c => !!c)
  if (codes.length === 0) return
  const projectCode = window.location.pathname.split('/')[2]
  const url = `/actualWork/${projectCode}?status=all&code=${codes.join(',')}`
  if (!isValidUrl(url)) {
    store.dispatch(
      addScreenMessage(store.getState().appFunction.currentExternalId!, {
        type: MessageLevel.WARN,
        title: intl.formatMessage({
          id: 'sprintreport.warning.invalid.url.length',
        }),
      })
    )
    return
  }
  open(url, undefined, undefined, true)
}

const formatValue = (value: number, format: string): string =>
  numeral(value).format(format)

type SprintReportGridContext = {
  pageState: SprintReportPageState
  workloadUnitState: WorkLoadUnitState
}

type CreateReportColumnDefParams = {
  colId: string
  field: string
  dateTerm: DateRange
  pinned: 'left' | 'right' | boolean
  lockPosition: 'left' | 'right' | undefined
  headerName: string
  headerClass: string
  searchCondition: SprintReportSearchCondition
  workingDayCalendars: OrganizationWorkingDayCalendarDetail[]
}

const createReportColumnDef = ({
  colId,
  field,
  dateTerm,
  pinned,
  lockPosition,
  headerName,
  headerClass,
  searchCondition,
  workingDayCalendars,
}: CreateReportColumnDefParams): ColGroupDef => {
  const from = dateVoService.toDate(dateTerm.from)
  const to = dateVoService.toDate(dateTerm.to)
  let businessDays = 0
  let workHours = 0
  workingDayCalendars.forEach(c => {
    const date = new Date(c.date)
    if (from <= date && date <= to && c.workHour > 0) {
      businessDays++
      workHours += c.workHour
    }
  })

  // 動的日付カラムの field は 'YYYY-MM-DD' で SprintReportRow のキーではないため、ColDef のジェネリックは外している
  const child: ColDef = {
    colId,
    headerName: getSubHeaderName(
      businessDays,
      workHours,
      searchCondition.timeGrain
    ),
    headerClass,
    field,
    width: 90,
    type: [ColumnType.number],
    lockPosition,
    pinned,
    floatingFilter: true,
    valueGetter: (params: ValueGetterParams<SprintReportRow>) => {
      if (!params.data) return ''
      const context: SprintReportGridContext = params.context
      const { aggregateField } = context.pageState
      const sum = summarizeValues(
        params.data.values,
        { from, to },
        aggregateField
      )
      if (isRatingCell(params.data)) {
        if (!sum.denominator) return NO_VALUE_STR
        return roundNumber(sum.numerator / sum.denominator, 2)
      }
      if (aggregateField === AggregateField.WBS_ITEM_WORKLOAD) {
        return roundNumber(
          sum.numerator / context.workloadUnitState.hoursPerSelectedUnit,
          2
        )
      }
      if (isWorkloadOnlyCell(params.data)) return NO_VALUE_STR
      return sum.numerator
    },
    valueFormatter: (params: ValueFormatterParams<SprintReportRow>): string => {
      if (!params.data) return ''
      const context: SprintReportGridContext = params.context
      const { aggregateField } = context.pageState
      const useWorkloadFormat =
        aggregateField === AggregateField.WBS_ITEM_WORKLOAD ||
        isRatingCell(params.data)
      const format = useWorkloadFormat
        ? DISPLAY_FORMAT_WORKLOAD
        : DISPLAY_FORMAT_COUNT
      if (typeof params.value !== 'number') return params.value ?? ''
      return formatValue(params.value, format)
    },
    onCellClicked: event => {
      if (!event.node.data) return
      if (event.node.data.key === SprintReportKey.TOTAL_ESTIMATED_HOUR) return
      openWorkResult(event.node.data.values, { from, to })
    },
    cellStyle: (params: CellClassParams<SprintReportRow>) => {
      if (!params.data) return { justifyContent: 'flex-end' }
      const context: SprintReportGridContext = params.context
      const { aggregateField } = context.pageState
      const sum = summarizeValues(
        params.data.values,
        { from, to },
        aggregateField
      )
      const isClickable =
        !isRatingCell(params.data) &&
        sum.wbsItemCodes &&
        sum.wbsItemCodes.length > 0 &&
        params.data.key !== SprintReportKey.TOTAL_ESTIMATED_HOUR
      const base: Record<string, string> = isClickable
        ? { justifyContent: 'flex-end', cursor: 'pointer', color: 'blue' }
        : { justifyContent: 'flex-end' }
      if (isRatingCell(params.data)) {
        const color = getCellColor(sum)
        if (color) base.backgroundColor = color
      }
      return base
    },
    cellRenderer: (params: ICellRendererParams<SprintReportRow>) => {
      if (!params.data) return null
      const context: SprintReportGridContext = params.context
      const { aggregateField } = context.pageState
      const sum = summarizeValues(
        params.data.values,
        { from, to },
        aggregateField
      )
      const disabled = !sum.wbsItemCodes || sum.wbsItemCodes.length === 0
      if (
        searchCondition.timeGrain !== TimeGrain.DAY &&
        params.data.key === SprintReportKey.TOTAL_ESTIMATED_HOUR
      ) {
        return (
          <TotalCellRenderer
            {...(params as ICellRendererParams)}
            dateTerm={{ from, to }}
            rootUuid={searchCondition.rootWbsItemUuid}
            teamUuid={searchCondition.teamUuid}
            searchConditionDateTerm={searchCondition.dateTerm}
            onClickValue={openWorkResult}
            disabled={disabled}
          />
        )
      }
      return <DefaultCellRenderer {...params} />
    },
  }

  return {
    groupId: generateUuid(),
    headerName,
    headerClass,
    children: [child],
  }
}
